import React, { Fragment, memo, useCallback, useEffect, useReducer, useState } from 'react'
import { IconButton, Stack, Button, Typography, Grid, Skeleton, Chip, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material'
import { Add, ArrowDownward, ArrowUpward, ExpandLess, ExpandMore, Search } from '@mui/icons-material'
// import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box } from '@mui/system';
import SubmitResourceDialog from './SubmitResourceDialog.js';
import FileList from './ResourceList/FileList.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getResourceLibraryFilesByCategory } from '../data/documents.js';

function ResourceCategoryList(props = {
  category: '', 
  categoriesList: [],
  files: [], 
  onClickAdd: undefined, 
  working: false,
  aggregatedView: false,
  filter: undefined, 
  onClearFilter: undefined,
  onEditResourceProperties: undefined,
  onDeleteDocument: undefined,
  onApproveDraft: undefined,
  onSelectTag: undefined,
  suppressTags: false,
  supportResourceUpload: false,
  onResourceUploaded: undefined,
  suppressFavorites: false,
  expand: true,
  onSetSearchScope: undefined,
  isManager: false,
  driveConfig: undefined,
  lazyLoadFiles: false
}) {
  const { category, categoriesList, supportResourceUpload, 
    onResourceUploaded, expand = true, onSetSearchScope,
    lazyLoadFiles = false
  } = props

  const queryClient = useQueryClient()


  const filesSorter = (_files, sortBy = 'created', sortDir = 'desc') => {
    switch (sortBy) {
      case 'created':
        return _files.sort((a,b) => {
          let at = new Date(a.createdDateTime).getTime()
          let bt = new Date(b.createdDateTime).getTime()
          return (sortDir === 'desc') ? bt - at : at - bt
        })
      case 'title':
        return _files.sort((a,b) => {
          let at = a.fields.Title ?? a.fields.LinkFilename
          let bt = b.fields.Title ?? b.fields.LinkFilename
          return (sortDir === 'desc') ? bt.localeCompare(at) : at.localeCompare(bt)
        })
    }
  }

  const filesReducer = (state, action) => {
    const newState = structuredClone(state)

    const resolveConfig = (fromState, forCategory, forFiles) => {
      const config = fromState.includedCategories.find((c) => c.category === forCategory)
      if (config) {
        if (forFiles) config.files = forFiles
        return config
      } else {
        // create from defaults
        return {
          category: forCategory,
          files: forFiles ?? fromState.files,
          expanded: fromState.expanded,
          limit: fromState.limit,
          page: fromState.page,
          sortBy: fromState.sortBy,
          sortDir: fromState.sortDir
        }
      }
    }

    switch (action?.type ?? '') {
      case 'files':
        let refreshedFiles = [...action.files]
        if (state.files.length && state.limit !== -1) {
          refreshedFiles = refreshedFiles.filter(file => 
            state.files.some(f => f.driveItem.id === file.driveItem.id))
        }
        newState.files = refreshedFiles
        newState.includedCategories = (() => {
          if (category && !props.aggregatedView) {
            const config = resolveConfig(state, category, newState.files)
            filesSorter(config.files, config.sortBy, config.sortDir)
            return [config]
          }
          if (newState.files.length > 0) {
            const catFiles = newState.files.reduce((map, file) => {
              const cat = file.fields.QSEPortalResourceCategory.replace(/category:/,'')
              if (!map[cat]) map[cat] = []
              map[cat].push(file)
              return map
            }, {})
            
            const configs = Object.entries(catFiles).map(([c, _files]) => {
              const config = resolveConfig(state, c, _files)
              filesSorter(config.files, config.sortBy, config.sortDir)
              return config
            })

            return configs.sort((a,b) => a.category.localeCompare(b.category))
          }
          return []
        })()
        break
      case 'limit':
      case 'page':
      case 'sort':
      case 'expand':
        const config = resolveConfig(newState, action.category)
        config.limit = action.limit ?? config.limit
        config.page = action.page ?? config.page
        config.sortBy = action.sortBy ?? config.sortBy
        config.sortDir = action.sortDir ?? config.sortDir
        config.expanded = action.expanded ?? config.expanded
        if (config.expanded) filesSorter(config.files, config.sortBy, config.sortDir)
        break
      case 'expandAll':
        newState.includedCategories.forEach((c) => {
          c.expanded = action.expanded
        })
        break
    }
    return newState

  }
  const defaultCategoryConfig = category && lazyLoadFiles ? [{ category, files: [], expanded: expand, limit: 5, page: 1, sortBy: 'created', sortDir: 'desc'}] : []
  const defaultDisplayState = { files: [], expanded: expand, limit: 5, page: 1, sortBy: 'created', sortDir: 'desc', includedCategories: defaultCategoryConfig }
  const [displayState, dispatchDisplayState] = useReducer(filesReducer, defaultDisplayState)

  const loadFilesForCategory = async ({ queryKey }) => {
    const [_key ] = queryKey
    
    return getResourceLibraryFilesByCategory(category, props.driveConfig)
  }

  const { data: queryFiles, isLoading, isError, error } = useQuery({
    queryKey: ['category-files', props.driveConfig.route, category],
    queryFn: loadFilesForCategory,
    enabled: lazyLoadFiles && Boolean(displayState.includedCategories[0].expanded),
    staleTime: 5 * 60 * 1000,   // 5 minutes
    cacheTime: 30 * 60 * 1000,  // 30 minutes
    suspense: false
  })

  const handleDeselectTag = (tag) => {
    if (!props.suppressTags && props.onSelectTag !== undefined) {
      if (props.filter) {
        const _t = props.filter.split(',')
        const _i = _t.findIndex(_tag => _tag === tag)
        if (_i > -1) _t.splice(_i, 1)
        if (_t.length > 0) {
          props.onSelectTag(_t.join(','))
        } else {
          props.onClearFilter()
        }
      }
    }
  }

  useEffect(() => {
    if (props.files && !lazyLoadFiles) {
      const _files = props.files
      // resolveAnalytics(_files)
      dispatchDisplayState({ type: 'files', files: _files})
    }

  }, [props.files, lazyLoadFiles, dispatchDisplayState])

  useEffect(() => {
    if (lazyLoadFiles) {
      const isExpanded = displayState.includedCategories[0].expanded
      if (isExpanded && queryFiles) {
        dispatchDisplayState({ type: 'files', files: queryFiles})
      }
      if (!isExpanded && queryFiles) {
        queryClient.invalidateQueries({queryKey: ['category-files', props.driveConfig.route, category]})
      }
    }

  }, [lazyLoadFiles, displayState.includedCategories[0]?.expanded || displayState.expand, queryFiles, dispatchDisplayState])

  useEffect(() => {
    dispatchDisplayState({type: 'expandAll', expanded: expand > 0})
  }, [expand])

  const getFilterCategoryLayout = useCallback((filter) => {
    return (
      <Stack direction="row" gap={1} alignItems='center' paddingTop={1}>
      <Typography variant='subtitle1' fontWeight='bold'>Includes:</Typography>
      { filter.split(',').map((f) => 
        <Chip size='small' key={f} label={f.replace(/tag:/,'')} onDelete={() => handleDeselectTag(f)}  />
      )
      }
      </Stack>
    )
  })

  const pageNumberCallback = useCallback((category, page) => {
    dispatchDisplayState({type: 'page', category: category, page: page + 1})
  })
  const pageSizeCallback = useCallback((category, size) => {
    dispatchDisplayState({type: 'limit', category: category, limit: size})
  })
  const sortDirButtonCallback = useCallback((category, sortDir) => (e) => {
    e.stopPropagation()
    dispatchDisplayState({type: 'sort', category: category, sortDir: sortDir})
  })
  const sortByButtonCallback = useCallback((category, sortBy) => (e) => {
    e.stopPropagation()
    dispatchDisplayState({type: 'sort', category: category, sortBy: sortBy})
  })


  const [isDragging, setIsDragging] = useState(false)
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const [categoryResourceDialogOpen, setCategoryResourceDialogOpen] = useState(false)

  const CategoryResourceZone = React.memo((props={resourceCategory: undefined, dialogOpen: false, setDialogOpen: undefined, isManager: false, driveConfig: undefined}) => {
    const [open, setOpen] = useState(props.dialogOpen)
    const [chosenFile, setChosenFile] = useState(null)

    const handleDrop = (e) => {
      e.preventDefault();

      if (!supportResourceUpload) {
        return
      }
      
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        setChosenFile(droppedFile);
        setOpen(true)
        // setOpen(true);
      } else {
        setIsDragging(false);
      }
    };
    const handleDragLeave = () => {
      setIsDragging(false);
    };
    const handleDialogClose = (navToMyResources, fileTouched) => {
      setChosenFile(null)
      setIsDragging(false)
      if (props.setDialogOpen !== undefined) {
        props.setDialogOpen(false)
      }
      setOpen(false)
      if (onResourceUploaded !== undefined) {
        onResourceUploaded(navToMyResources, fileTouched)
      }
    }

    return (
      <Box sx={{ border: supportResourceUpload && isDragging ? '2px dashed blue' : 'unset' }}
        onDrop={handleDrop}
      >
        <SubmitResourceDialog open={open} 
          onClose={handleDialogClose} 
          resourceCategories={ categoriesList && categoriesList.length ? categoriesList : [category]}
          selectedCategory={props.resourceCategory}
          selectedFile={chosenFile}
          isManager={props.isManager}
          driveConfig={props.driveConfig}
        />
        
        { props.children }
    </Box>
  )})

  return (
    <Fragment key={props.filter ? props.filter : category}>
    { props.filter && getFilterCategoryLayout(props.filter)}

    { displayState.includedCategories.map((categoryConfig) => 
    <CategoryResourceZone key={categoryConfig.category} resourceCategory={categoryConfig.category} dialogOpen={categoryResourceDialogOpen} setDialogOpen={setCategoryResourceDialogOpen} isManager={props.isManager} driveConfig={props.driveConfig}>
      <Box 
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      >        
        <Accordion disableGutters expanded={Boolean(categoryConfig.expanded)} elevation={0} onChange={(event, newExpanded) => dispatchDisplayState({type: 'expand', category: categoryConfig.category, expanded: newExpanded}) //setExpanded(newExpanded)
      }>
          <AccordionSummary disableRipple
            sx={{ padding: '2px', minHeight: '25px', '& .MuiAccordionSummary-content': { margin: 'unset'}}}>
          <Grid container gap={0} padding='3px 0' justifyItems='end' alignItems='center'>
            <Grid item xs={12}>
              <Stack direction='row' justifyContent='space-between'>                  
                <Stack direction='row' columnGap={1} alignItems='center'>
                  <Typography variant='subtitle1' fontWeight='bold'>{categoryConfig.category}</Typography>
                  <Tooltip placement='top-start' title={`Click (anywhere on the title bar) to ${categoryConfig.expanded ? 'collapse' : 'expand'} ${categoryConfig.category}`}>
                    <>
                    {categoryConfig.expanded && <IconButton size='small'><ExpandLess /></IconButton>}
                    {!categoryConfig.expanded && <IconButton size='small'><ExpandMore /></IconButton>}
                    </>
                  </Tooltip>
                </Stack>
                <Stack direction="row-reverse" gap={.5} alignItems='center'>
                  {categoryConfig.expanded && <>                      
                    { ['asc', 'desc'].map((sortDir) => {
                      return (
                      <Tooltip key={sortDir} arrow placement='left' 
                        title={`${categoryConfig.sortDir === sortDir ? '(Current) ': ''}Sort files ${sortDir}ending by ${categoryConfig.sortBy}${categoryConfig.sortBy === 'created' ? ' date' : ''}`}
                      >
                        <span>
                        <IconButton disabled={categoryConfig.sortDir === sortDir} variant='text' size='small' 
                        style={{padding: '1px !important', minWidth: 'unset'}}
                        onClick={sortDirButtonCallback(categoryConfig.category, sortDir)}
                        >{sortDir === 'asc' ? <ArrowUpward sx={{ maxHeight: '20px'}}/> : <ArrowDownward sx={{ maxHeight: '20px'}}/>}</IconButton>
                        </span>
                      </Tooltip>)
                    }
                    )}
                    { ['title', 'created'].map((sortBy) => 
                      <Tooltip key={sortBy} arrow placement='left' 
                        title={`${categoryConfig.sortBy === sortBy ? '(Current) ': ''}Sort files by ${sortBy}${sortBy === 'created' ? ' date' : ''}, ${categoryConfig.sortDir}ending`}
                      >
                        <span>
                        <Button disabled={categoryConfig.sortBy === sortBy} variant='text' size='small' 
                          style={{textTransform: 'capitalize', padding: '2px !important', minWidth: 'unset'}}
                          onClick={sortByButtonCallback(categoryConfig.category, sortBy)}
                        >{sortBy}</Button>
                        </span>
                      </Tooltip>
                    )}                      
                    <Typography variant='caption' lineHeight='1.75'>Sort By:</Typography>
                    {!props.filter && <>
                      { onSetSearchScope && 
                        <Tooltip arrow placement='left' title={`Start a search with ${categoryConfig.category}`}>
                          <IconButton size='small' onClick={(e) => { e.stopPropagation(); onSetSearchScope(`category:${categoryConfig.category}`)}}>
                            <Search />
                          </IconButton>
                        </Tooltip>
                      }
                      { supportResourceUpload &&
                        <Tooltip arrow placement='left' title={`Submit a Resource for ${categoryConfig.category}`}>
                        <IconButton size='small' onClick={(e) => { e.stopPropagation(); setCategoryResourceDialogOpen(true)}}>
                          <Add />
                        </IconButton>
                        </Tooltip>
                      }                        
                    </>}
                  </>}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{padding: 'unset'}} >
          <Grid container gap={1} justifyItems='flex-start'>
            
              {(props.working || isLoading) && <Skeleton variant='rounded' animation='pulse' height={200} width='100%' />}
              {!(props.working || isLoading) && 
              categoryConfig.files && 
                <FileList
                  categoryConfig={categoryConfig}
                  filter={props.filter}
                  onApproveDraft={props.onApproveDraft}
                  onDeleteDocument={props.onDeleteDocument}
                  onEditResourceProperties={props.onEditResourceProperties}
                  onSelectTag={props.onSelectTag}
                  suppressFavorites={props.suppressFavorites}
                  suppressTags={props.suppressTags}
                  pageNumberCallback={pageNumberCallback}
                  pageSizeCallback={pageSizeCallback}
                  isManager={props.isManager}
                  driveConfig={props.driveConfig}
                  /> }
            
            
            {!(props.working || isLoading) && 
            categoryConfig.files.length === 0 
            && `No Results`}
          </Grid>
        </AccordionDetails>
        </Accordion>
      </Box>
    </CategoryResourceZone>
    )}
    </Fragment>
  )
}

  export default memo(ResourceCategoryList)